/**
 * @file Devices index (`#/devices`) — admin only.
 *
 * Global IT view of every device. Live query against /devices with
 * client-side filtering (category, status, holder search, context tag,
 * unassigned-only, orphan-only, free-text search by name/SN/notes).
 *
 * Table not card-grid — there are ~1000 devices and a grid is impractical.
 * Per UI spec.
 */

import { el, clear, formatDate } from '../utils.js';
import { Icons, categoryIcon }    from '../icons.js';
import { CATEGORIES, DEVICE_STATUSES, contextTagInfo } from '../config.js';
import { navigate }               from '../router.js';
import { watchDevices }           from '../db.js';
import { renderPageHead }         from '../page-header.js';
import { sortableTable, listCard } from '../lib/sortable-table.js';
import { copyable }               from '../lib/copyable.js';
import { renderLoadError }       from '../lib/load-error.js';

export function renderDevicesIndex(host) {
  clear(host);

  host.appendChild(renderPageHead(
    'Devices',
    "The global device pool — every device Abra (Flexi), Meraki MDM, IRU MDM, or the QA testing-devices sheet knows about. Cross-system flags surface here: an MDM holder mismatch, an MDM orphan that Abra doesn't have, a testing-sheet wrong holder, or a testing device that's not in Abra at all. Devices disposed in Abra live in the Decommissioned tab — a read-only registry, out of the active pool and out of search. Gear-only peripherals (keyboards, mice, etc.) live on the employee's /people page, not here.",
  ));

  // ── Filter bar ──────────────────────────────────────────────────────────
  const state = {
    tab:      'active', // 'active' | 'decommissioned'
    category: '',
    status:   '',
    source:   '',
    contextTag: '',
    unassignedOnly: false,
    orphanOnly: false,
    flagsOnly: false,   // surface only rows with a cross-system flag —
                        // Meraki holder mismatch, Meraki orphan, testing-
                        // sheet wrong holder, or testing not in Abra.
                        // The admin's "what needs reconciliation" queue.
    search: '',
  };

  // Tab segment: the active pool vs the decommissioned registry. The
  // registry keeps its own search (shared input below) but drops the
  // pool-specific filters — a dead device has no status/holder to filter by.
  const segPool  = el('button', { class: 'active' }, 'Active pool');
  const segDecom = el('button', null, 'Decommissioned');
  const setTab = (tab) => {
    state.tab = tab;
    segPool.classList.toggle('active', tab === 'active');
    segDecom.classList.toggle('active', tab === 'decommissioned');
    filtersEl.classList.toggle('pool-hidden', tab !== 'active');
    rerender();
  };
  segPool.addEventListener('click',  () => setTab('active'));
  segDecom.addEventListener('click', () => setTab('decommissioned'));

  const catSelect = el('select', null,
    el('option', { value: '' }, 'All categories'),
    ...Object.entries(CATEGORIES).map(([k, c]) => el('option', { value: k }, c.label)),
  );
  catSelect.addEventListener('change', () => { state.category = catSelect.value; rerender(); });

  const statusSelect = el('select', null,
    el('option', { value: '' }, 'All statuses'),
    ...Object.entries(DEVICE_STATUSES).map(([k, s]) => el('option', { value: k }, s.label)),
  );
  statusSelect.addEventListener('change', () => { state.status = statusSelect.value; rerender(); });

  // Source filter: Flexi (Abra), Meraki orphans, and testing-sheet
  // orphans. Gear-source devices are filtered out at the page level (see
  // applyFilters) and live on /me + /people/:id instead.
  const sourceSelect = el('select', null,
    el('option', { value: '' },        'All sources'),
    el('option', { value: 'flexi' },   'Abra (Flexi)'),
    el('option', { value: 'meraki' },  'Meraki orphans'),
    el('option', { value: 'iru' },     'IRU orphans'),
    el('option', { value: 'testing' }, 'Testing orphans'),
  );
  sourceSelect.addEventListener('change', () => { state.source = sourceSelect.value; rerender(); });

  const tagSelect = el('select', null,
    el('option', { value: '' },        'All tags'),
    el('option', { value: 'TESTING' }, 'Testing'),
    el('option', { value: 'Storage' }, 'Storage'),
    el('option', { value: 'Lab' },     'Lab'),
    el('option', { value: 'Spare' },   'Spare'),
  );
  tagSelect.addEventListener('change', () => { state.contextTag = tagSelect.value; rerender(); });

  const unassignedToggle = el('button', { class: 'btn filter-toggle' }, 'Unassigned only');
  unassignedToggle.addEventListener('click', () => {
    state.unassignedOnly = !state.unassignedOnly;
    if (state.unassignedOnly) state.orphanOnly = false;
    unassignedToggle.classList.toggle('active', state.unassignedOnly);
    orphanToggle.classList.toggle('active', state.orphanOnly);
    rerender();
  });

  const orphanToggle = el('button', { class: 'btn filter-toggle' }, 'Orphan only');
  orphanToggle.addEventListener('click', () => {
    state.orphanOnly = !state.orphanOnly;
    if (state.orphanOnly) state.unassignedOnly = false;
    orphanToggle.classList.toggle('active', state.orphanOnly);
    unassignedToggle.classList.toggle('active', state.unassignedOnly);
    rerender();
  });

  // Flags filter — the "what needs reconciliation" queue. Matches every
  // cross-system flag we surface today: Meraki holder mismatch, Meraki
  // orphan (not in Abra), testing-sheet wrong holder, testing not in Abra.
  // All four resolve to a "fix something in Abra" admin action.
  const flagsToggle = el('button', { class: 'btn filter-toggle' }, 'Flags');
  flagsToggle.addEventListener('click', () => {
    state.flagsOnly = !state.flagsOnly;
    flagsToggle.classList.toggle('active', state.flagsOnly);
    rerender();
  });

  const searchInput = el('input', { type: 'search', class: 'input-serial', placeholder: 'Search SN, name, holder…' });
  searchInput.addEventListener('input', () => {
    state.search = searchInput.value.trim().toLowerCase();
    rerender();
  });

  // Pool-only filters live in their own span so the tab switch can hide
  // them wholesale; the search input stays for both tabs.
  const poolFilters = el('span', { class: 'filters-advanced' },
    catSelect, statusSelect, sourceSelect, tagSelect,
    unassignedToggle, orphanToggle, flagsToggle,
  );
  // Phones fold the seven pool filters behind one button; search stays out.
  const moreBtn = el('button', { class: 'btn filter-toggle filters-more', 'aria-expanded': 'false' }, 'Filters');
  const filtersEl = el('div', { class: 'filters' },
    el('div', { class: 'segment' }, segPool, segDecom),
    moreBtn,
    poolFilters,
    searchInput,
  );
  moreBtn.addEventListener('click', () => {
    const open = filtersEl.classList.toggle('filters-open');
    moreBtn.classList.toggle('active', open);
    moreBtn.setAttribute('aria-expanded', String(open));
  });
  host.appendChild(filtersEl);

  // ── Table host ──────────────────────────────────────────────────────────
  const tableHost = el('div', { class: 'card', style: 'padding:0' });
  host.appendChild(tableHost);
  // Initial skeleton so the user sees the page is doing something even if
  // Firestore takes a moment (or never fires).
  tableHost.appendChild(el('div', { class: 'skeleton', style: 'height:200px;margin:24px' }));

  let lastDevices = [];
  const employeesById = new Map(); // populated lazily — for holder names

  function rerender() {
    clear(tableHost);
    const filtered = applyFilters(lastDevices, state, employeesById);
    if (!filtered.length) {
      const isDecom = state.tab === 'decommissioned';
      tableHost.appendChild(el('div', { class: 'empty' },
        Icons.search(),
        el('p', null, lastDevices.length
          ? (isDecom && !state.search ? 'No decommissioned devices yet.' : 'No devices match those filters.')
          : 'Nothing in the device pool yet.'),
        el('p', { class: 'muted' }, lastDevices.length
          ? (isDecom && !state.search
              ? 'Devices marked "Úplné vyřazení" in Abra land here after the next sync.'
              : 'Loosen the filters or clear the search.')
          : 'Run the Flexi sync to pull the master list.'),
      ));
      return;
    }
    const safeRow = (renderer, cols) => (d) => {
      try { return renderer(d); }
      catch (err) {
        console.error('[devices] row failed', d.id, err);
        return el('tr', { class: 'row-alert' },
          el('td', { colspan: cols, class: 'muted' }, `Failed to render device ${d.id}: ${err.message}`));
      }
    };

    if (state.tab === 'decommissioned') {
      // Registry view: what it was, who had it last, when Abra wrote it
      // off, and whether an MDM still thinks it's enrolled (the actionable
      // bit — sortable so the "still enrolled" rows group together).
      const lastHolderName = (d) => (d.lastHolder
        ? (employeesById.get(d.lastHolder)?.fullName || d.lastHolderEmail || d.lastHolder)
        : (d.lastHolderEmail || ''));
      tableHost.appendChild(sortableTable({
        columns: [
          { label: 'Category',    sortValue: (d) => CATEGORIES[d.category]?.label || d.category },
          { label: 'Name',        sortValue: (d) => d.name },
          { label: 'SN',          sortValue: (d) => d.sn },
          { label: 'Last holder', sortValue: lastHolderName },
          { label: 'Disposed on', sortValue: (d) => d.disposedAt },
          { label: 'Still in MDM', sortValue: (d) => (d.stillInMdm ? (d.mdmSource === 'iru' ? 'IRU' : 'Meraki') : null) },
        ],
        rows: filtered,
        renderRow: safeRow(decommissionedRow, 6),
        renderCard: decommissionedCard,
        initialSort: { index: 4, dir: 'desc' }, // freshest disposals first
      }));
      return;
    }

    const holderName = (d) => (d.holderUnresolved
      ? `zzz-orphan-${d.holderEmployeeNumberRaw || ''}` // orphans sort to the end
      : (d.currentHolder ? (employeesById.get(d.currentHolder)?.fullName || d.currentHolder) : ''));
    tableHost.appendChild(sortableTable({
      columns: [
        { label: 'Category', sortValue: (d) => CATEGORIES[d.category]?.label || d.category },
        { label: 'Name',     sortValue: (d) => d.name },
        { label: 'SN',       sortValue: (d) => d.sn },
        { label: 'Holder',   sortValue: holderName },
        { label: 'Status',   sortValue: (d) => DEVICE_STATUSES[d.status]?.label || d.status },
        { label: 'Tags',     sortable: false },
        { label: 'Source',   sortValue: (d) => d.source },
      ],
      rows: filtered,
      renderRow: safeRow(deviceRow, 7),
      renderCard: deviceCard,
    }));
  }

  function decommissionedRow(d) {
    const cat = CATEGORIES[d.category] || { label: d.category };
    const lastHolder = d.lastHolder
      ? (employeesById.get(d.lastHolder)?.fullName || d.lastHolderEmail || d.lastHolder)
      : (d.lastHolderEmail || '—');
    const mdmCell = d.stillInMdm
      ? el('span', {
          class: 'chip chip-error',
          title: `Still enrolled in ${d.mdmSource === 'iru' ? 'IRU' : 'Meraki'} — unenroll it, the device left the fleet`,
        }, d.mdmSource === 'iru' ? 'IRU' : 'Meraki')
      : el('span', { class: 'muted' }, '—');
    return el('tr', { class: d.stillInMdm ? 'row-warn' : null, 'data-row-link': true, onClick: () => navigate(`/devices/${d.id}`) },
      el('td', null, el('span', { style: 'display:inline-flex;align-items:center;gap:6px;white-space:nowrap' }, categoryIcon(d.category), cat.label)),
      el('td', null, d.name ? copyable(d.name, { label: 'name' }) : '—'),
      el('td', null, d.sn ? copyable(d.sn, { label: 'SN', mono: true }) : '—'),
      el('td', null, lastHolder),
      el('td', null, d.disposedAt ? formatDate(d.disposedAt) : '—'),
      el('td', null, mdmCell),
    );
  }

  function decommissionedCard(d) {
    const cat = CATEGORIES[d.category] || { label: d.category };
    const lastHolder = d.lastHolder
      ? (employeesById.get(d.lastHolder)?.fullName || d.lastHolderEmail || d.lastHolder)
      : (d.lastHolderEmail || null);
    return listCard({
      title: d.name || cat.label,
      meta:  [cat.label, d.sn ? el('span', { class: 'sn' }, d.sn) : null, lastHolder, d.disposedAt ? `Disposed ${formatDate(d.disposedAt)}` : null],
      aside: [d.stillInMdm ? el('span', { class: 'chip chip-error' }, d.mdmSource === 'iru' ? 'IRU' : 'Meraki') : null],
      onClick: () => navigate(`/devices/${d.id}`),
      class: d.stillInMdm ? 'row-warn' : '',
    });
  }

  const holderLabelFor = (d) => (d.holderUnresolved
    ? el('span', { class: 'chip chip-error' }, `Orphan: ${d.holderEmployeeNumberRaw || '?'}`)
    : (d.currentHolder ? (employeesById.get(d.currentHolder)?.fullName || d.currentHolder) : '—'));
  const sourceLabel = (d) => ({ flexi: 'Flexi', meraki: 'Meraki', iru: 'IRU', testing: 'Testing' }[d.source] || 'Gear');

  // Context-tag + reconcile chips and the row tint — shared by the table
  // row and the phone card.
  function deviceFlags(d) {
    const chips = (d.contextTags || []).map((t) => {
      const info = contextTagInfo(t);
      return el('span', { class: 'chip' }, info.label);
    });
    // Reconcile chips from the joined match status. Multiple flags can
    // coexist on one row (e.g. Meraki agrees, but testing sheet says the
    // device is on someone else's desk). Hovering each chip reveals the
    // per-source values for quick triage. The row's left border colour
    // escalates to the loudest active flag.
    const matchStatus = d.match?.status || null;
    const testingFlag = d.match?.testing?.status || null;
    const abraEmail   = d.abra?.holderEmail || null;
    const merakiEmail = d.meraki?.ownerEmail || null;
    let rowExtraClass = '';

    // Meraki-side chip (existing behaviour).
    if (matchStatus === 'orphan_no_abra') {
      chips.push(el('span', { class: 'chip chip-error' }, 'Not in Abra'));
      rowExtraClass = ' row-alert';
    } else if (matchStatus === 'holder_mismatch') {
      chips.push(el('span', {
        class: 'chip chip-warning',
        title: `Abra: ${abraEmail || '(none)'} · Meraki: ${merakiEmail || '(none)'}`,
      }, 'Meraki mismatch'));
      if (!rowExtraClass) rowExtraClass = ' row-warn';
    } else if (matchStatus === 'abra_unassigned') {
      chips.push(el('span', {
        class: 'chip',
        title: `Holder ${merakiEmail || '?'} sourced from Meraki (Abra has no assignment)`,
      }, 'From Meraki'));
    } else if (matchStatus === 'matched') {
      chips.push(el('span', { class: 'chip' }, 'Abra · Meraki'));
    } else if (matchStatus === 'testing_not_in_abra') {
      chips.push(el('span', {
        class: 'chip chip-error',
        title: `Sheet tab: ${d.testing?.sheetTab || '?'} · Used by: ${d.testing?.usedByName || '(none)'}`,
      }, 'Not in Abra (testing)'));
      rowExtraClass = ' row-alert';
    }

    // Testing-side chip — only on Flexi-rooted rows that ALSO appear in
    // the testing sheet. The orphan case (source='testing') is handled
    // by the matchStatus branch above so we don't double-chip it.
    if (testingFlag === 'wrong_holder') {
      const sheetWho = d.match?.testing?.sheetUsedByName || '(?)';
      const abraWho  = d.match?.testing?.abraHolderName  || abraEmail || '(?)';
      chips.push(el('span', {
        class: 'chip chip-warning',
        title: `Testing sheet says: ${sheetWho} · Abra says: ${abraWho} · Sheet: ${d.testing?.sheetTab || '?'}`,
      }, 'Testing mismatch'));
      if (!rowExtraClass) rowExtraClass = ' row-warn';
    }
    return { chips, rowClass: rowExtraClass.trim() || null };
  }

  function deviceRow(d) {
    const cat    = CATEGORIES[d.category] || { label: d.category };
    const status = DEVICE_STATUSES[d.status] || { label: d.status, badge: 'idle' };
    const { chips, rowClass } = deviceFlags(d);
    return el('tr', { class: rowClass, 'data-row-link': true, onClick: () => navigate(`/devices/${d.id}`) },
      el('td', null, el('span', { style: 'display:inline-flex;align-items:center;gap:6px;white-space:nowrap' }, categoryIcon(d.category), cat.label)),
      el('td', null, d.name ? copyable(d.name, { label: 'name' }) : '—'),
      el('td', null, d.sn ? copyable(d.sn, { label: 'SN', mono: true }) : '—'),
      el('td', null, holderLabelFor(d)),
      el('td', null, el('span', { class: `chip chip-${status.badge}` }, status.label)),
      el('td', null, el('div', { class: 'chips' }, ...chips)),
      el('td', { class: 'muted' }, sourceLabel(d)),
    );
  }

  function deviceCard(d) {
    const cat    = CATEGORIES[d.category] || { label: d.category };
    const status = DEVICE_STATUSES[d.status] || { label: d.status, badge: 'idle' };
    const { chips, rowClass } = deviceFlags(d);
    return listCard({
      title: d.name || cat.label,
      meta:  [cat.label, d.sn ? el('span', { class: 'sn' }, d.sn) : null, holderLabelFor(d)],
      aside: [
        el('span', { class: `chip chip-${status.badge}` }, status.label),
        chips.length ? el('div', { class: 'chips', style: 'justify-content:flex-end' }, ...chips) : null,
      ],
      onClick: () => navigate(`/devices/${d.id}`),
      class: rowClass || '',
    });
  }

  // ── Live data ──────────────────────────────────────────────────────────
  // We don't pre-filter at the Firestore level — the index would explode for
  // every filter combination and we'd hit "missing index" errors. ~1000 rows
  // is well within Firestore's snapshot size; filtering is client-side.
  const onLoadError = () => {
    clear(tableHost);
    tableHost.appendChild(renderLoadError('Could not load the device pool.'));
  };
  const unsub = watchDevices({}, async (devices) => {
    lastDevices = devices;
    // Lazy-load employee names for displayed holders. Skip if everything
    // unresolved/null — most pages render before this fires. Includes the
    // decommissioned registry's lastHolder ids so that tab shows names too.
    const holderIds = [...new Set(devices.flatMap((d) => [d.currentHolder, d.lastHolder]).filter(Boolean))];
    const missing = holderIds.filter((id) => !employeesById.has(id));
    if (missing.length) {
      try {
        const { getEmployee } = await import('../db.js');
        await Promise.all(missing.map(async (id) => {
          const e = await getEmployee(id);
          if (e) employeesById.set(id, e);
        }));
      } catch (err) {
        console.warn('[devices] could not hydrate holder names', err);
      }
    }
    rerender();
  }, onLoadError);
  host.addEventListener('cleanup', unsub, { once: true });
}

function applyFilters(devices, state, employeesById = new Map()) {
  // Resolve a device's holder into searchable text — the placeholder says
  // "Search SN, name, holder…" and "who has what" is the most common admin
  // lookup, but holder was never in the haystack, so those searches returned
  // a flat "No devices match" on a 1200-device list.
  const holderText = (d) => {
    const parts = [d.currentHolderEmail, d.lastHolderEmail, d.holderEmployeeNumberRaw];
    if (d.currentHolder) parts.push(employeesById.get(d.currentHolder)?.fullName);
    if (d.lastHolder)    parts.push(employeesById.get(d.lastHolder)?.fullName);
    return parts.filter(Boolean).join(' ');
  };
  // Decommissioned tab: its own little world — only registry rows, only the
  // free-text search applies (the pool filters are hidden while it's open).
  if (state.tab === 'decommissioned') {
    return devices.filter((d) => {
      if (d.status !== 'decommissioned') return false;
      if (state.search) {
        const hay = [d.name, d.sn, d.flexiKod, d.notes, d.gearNotes, holderText(d)]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(state.search)) return false;
      }
      return true;
    });
  }

  return devices.filter((d) => {
    // Gear-source devices (peripherals created at onboarding handover, no
    // Abra/Meraki counterpart) don't belong in the global device pool —
    // they're a per-employee thing and surface only on /me and /people/:id.
    // The /devices index is the canonical "inventory across all systems"
    // view; admitting Gear-only items would imply they're tracked
    // somewhere, which they aren't.
    if (d.source === 'gear') return false;
    // Decommissioned devices live exclusively in their own tab.
    if (d.status === 'decommissioned') return false;
    if (state.category && d.category !== state.category) return false;
    if (state.status   && d.status   !== state.status)   return false;
    if (state.source   && d.source   !== state.source)   return false;
    // "IRU / Meraki orphans" means company devices missing from Abra —
    // personal laptops are found via the Personal status instead.
    if ((state.source === 'iru' || state.source === 'meraki') && d.personal) return false;
    if (state.contextTag) {
      const has = (d.contextTags || []).some((t) => t.toLowerCase() === state.contextTag.toLowerCase());
      if (!has) return false;
    }
    if (state.unassignedOnly && (d.currentHolder || d.holderUnresolved)) return false;
    if (state.orphanOnly && !d.holderUnresolved) return false;
    if (state.flagsOnly) {
      const ms = d.match?.status;
      const tf = d.match?.testing?.status;
      const flagged = ms === 'orphan_no_abra'
                   || ms === 'holder_mismatch'
                   || ms === 'testing_not_in_abra'
                   || tf === 'wrong_holder';
      if (!flagged) return false;
    }
    if (state.search) {
      const hay = [
        d.name, d.sn, d.flexiKod, d.notes, d.gearNotes, holderText(d),
        d.testing?.deviceTag, d.testing?.imei, d.testing?.usedByName, d.testing?.sheetTab,
        ...(d.contextTags || []),
      ].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(state.search)) return false;
    }
    return true;
  });
}
